import React, { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { AlertCircle, Info, DollarSign, ClipboardList, Target, ChevronLeft, ChevronRight } from 'lucide-react';
import { TextInput } from '../../../shared/components/TextInput';
import { Textarea } from '../../../shared/components/Textarea';
import { AutocompleteSelect } from '../../../shared/components/AutocompleteSelect';
import { Alert } from '../../../shared/components/Alert';
import { listarClientes, listarContatos } from '../../clientes/api';
import { listarUsuarios } from '../../usuarios/api';
import { useEtapas, useOrigens, useTipos } from '../hooks';
import type {
  CreateOportunidadePayload,
  Oportunidade,
  Prioridade,
  UpdateOportunidadePayload,
} from '../api';

const PRIORIDADE_OPTIONS: { value: Prioridade; label: string }[] = [
  { value: 'baixa', label: 'Baixa' },
  { value: 'media', label: 'Média' },
  { value: 'alta', label: 'Alta' },
  { value: 'critica', label: 'Crítica' },
];

/**
 * Estado interno do formulário. Todos os campos como string para
 * facilitar bind com inputs; o `buildPayload` normaliza tipos ao
 * emitir.
 */
export interface OpportunityFormState {
  nome: string;
  clientId: string;
  contatoPrincipalId: string;
  responsavelId: string;
  origemId: string;
  tipoId: string;
  stageId: string;
  prioridade: Prioridade;
  valorEstimado: string;
  custoEstimado: string;
  moeda: string;
  probabilidade: string;
  expectedClosingDate: string;
  nextActionDate: string;
  nextActionDescription: string;
  produtoServico: string;
  necessidade: string;
  escopo: string;
  isConfidential: boolean;
  observacoes: string;
}

const EMPTY_STATE: OpportunityFormState = {
  nome: '',
  clientId: '',
  contatoPrincipalId: '',
  responsavelId: '',
  origemId: '',
  tipoId: '',
  stageId: '',
  prioridade: 'media',
  valorEstimado: '',
  custoEstimado: '',
  moeda: 'BRL',
  probabilidade: '',
  expectedClosingDate: '',
  nextActionDate: '',
  nextActionDescription: '',
  produtoServico: '',
  necessidade: '',
  escopo: '',
  isConfidential: false,
  observacoes: '',
};

function stateFromOportunidade(o: Oportunidade): OpportunityFormState {
  return {
    nome: o.nome,
    clientId: o.clientId,
    contatoPrincipalId: o.contatoPrincipalId,
    responsavelId: o.responsavelId,
    origemId: o.origemId,
    tipoId: o.tipoId,
    stageId: o.stageId,
    prioridade: o.prioridade,
    valorEstimado: o.valorEstimado ?? '',
    custoEstimado: o.custoEstimado ?? '',
    moeda: o.moeda,
    probabilidade: o.probabilidade != null ? String(o.probabilidade) : '',
    expectedClosingDate: o.expectedClosingDate ?? '',
    nextActionDate: o.nextActionDate ? o.nextActionDate.slice(0, 16) : '',
    nextActionDescription: o.nextActionDescription ?? '',
    produtoServico: o.produtoServico,
    necessidade: o.necessidade,
    escopo: o.escopo ?? '',
    isConfidential: o.isConfidential,
    observacoes: o.observacoes ?? '',
  };
}

/**
 * Converte o estado do form em payload de create (todos os campos)
 * ou de update (só o que difere do valor original — reduz churn na
 * trilha de auditoria).
 */
export function buildCreatePayload(
  state: OpportunityFormState,
): CreateOportunidadePayload {
  return {
    nome: state.nome.trim(),
    clientId: state.clientId,
    contatoPrincipalId: state.contatoPrincipalId,
    responsavelId: state.responsavelId,
    origemId: state.origemId,
    tipoId: state.tipoId,
    stageId: state.stageId || undefined,
    prioridade: state.prioridade,
    valorEstimado: state.valorEstimado || undefined,
    custoEstimado: state.custoEstimado || undefined,
    moeda: state.moeda || 'BRL',
    probabilidade: state.probabilidade ? Number(state.probabilidade) : undefined,
    expectedClosingDate: state.expectedClosingDate || undefined,
    nextActionDate: state.nextActionDate
      ? new Date(state.nextActionDate).toISOString()
      : undefined,
    nextActionDescription: state.nextActionDescription || undefined,
    produtoServico: state.produtoServico.trim(),
    necessidade: state.necessidade.trim(),
    escopo: state.escopo.trim() || undefined,
    isConfidential: state.isConfidential || undefined,
    observacoes: state.observacoes.trim() || undefined,
  };
}

export function buildUpdatePayload(
  state: OpportunityFormState,
  original: Oportunidade,
): UpdateOportunidadePayload {
  const p: UpdateOportunidadePayload = {};
  if (state.nome.trim() !== original.nome) p.nome = state.nome.trim();
  if (state.contatoPrincipalId !== original.contatoPrincipalId)
    p.contatoPrincipalId = state.contatoPrincipalId;
  if (state.responsavelId !== original.responsavelId)
    p.responsavelId = state.responsavelId;
  if (state.origemId !== original.origemId) p.origemId = state.origemId;
  if (state.tipoId !== original.tipoId) p.tipoId = state.tipoId;
  if (state.prioridade !== original.prioridade) p.prioridade = state.prioridade;
  const ve = state.valorEstimado || undefined;
  if (ve !== (original.valorEstimado ?? undefined)) p.valorEstimado = ve;
  const ce = state.custoEstimado || undefined;
  if (ce !== (original.custoEstimado ?? undefined)) p.custoEstimado = ce;
  if (state.moeda !== original.moeda) p.moeda = state.moeda;
  const prob = state.probabilidade ? Number(state.probabilidade) : undefined;
  if (prob !== (original.probabilidade ?? undefined)) p.probabilidade = prob;
  const ecd = state.expectedClosingDate || undefined;
  if (ecd !== (original.expectedClosingDate ?? undefined)) p.expectedClosingDate = ecd;
  const nad = state.nextActionDate
    ? new Date(state.nextActionDate).toISOString()
    : undefined;
  if (nad !== (original.nextActionDate ?? undefined)) p.nextActionDate = nad;
  const nadesc = state.nextActionDescription || undefined;
  if (nadesc !== (original.nextActionDescription ?? undefined))
    p.nextActionDescription = nadesc;
  if (state.produtoServico.trim() !== original.produtoServico)
    p.produtoServico = state.produtoServico.trim();
  if (state.necessidade.trim() !== original.necessidade)
    p.necessidade = state.necessidade.trim();
  const es = state.escopo.trim() || undefined;
  if (es !== (original.escopo ?? undefined)) p.escopo = es;
  if (state.isConfidential !== original.isConfidential)
    p.isConfidential = state.isConfidential;
  const obs = state.observacoes.trim() || undefined;
  if (obs !== (original.observacoes ?? undefined)) p.observacoes = obs;
  return p;
}

/**
 * Valida estado do formulário para `create/effective`. Devolve
 * mapa de erros por campo. Rascunho aceita quase tudo em branco —
 * validação leve separada (`validateDraft`).
 */
export function validateForm(state: OpportunityFormState): Record<string, string> {
  const errors: Record<string, string> = {};
  if (!state.nome.trim()) errors.nome = 'Nome é obrigatório.';
  if (!state.clientId) errors.clientId = 'Cliente é obrigatório.';
  if (!state.contatoPrincipalId)
    errors.contatoPrincipalId = 'Contato principal é obrigatório.';
  if (!state.responsavelId) errors.responsavelId = 'Responsável comercial é obrigatório.';
  if (!state.origemId) errors.origemId = 'Origem é obrigatória.';
  if (!state.tipoId) errors.tipoId = 'Tipo é obrigatório.';
  if (!state.produtoServico.trim())
    errors.produtoServico = 'Produto/Serviço é obrigatório.';
  if (state.necessidade.trim().length < 10)
    errors.necessidade = 'Necessidade deve ter pelo menos 10 caracteres.';
  if (state.probabilidade) {
    const n = Number(state.probabilidade);
    if (!Number.isFinite(n) || n < 0 || n > 100)
      errors.probabilidade = 'Probabilidade deve estar entre 0 e 100.';
  }
  return errors;
}

// ---------------------------------------------------------------------

type TabId = 'geral' | 'financeiro' | 'necessidade';
const TABS: { id: TabId; label: string; icon: React.ReactNode }[] = [
  { id: 'geral', label: 'Dados Gerais', icon: <Info className="h-4 w-4" /> },
  { id: 'financeiro', label: 'Financeiro', icon: <DollarSign className="h-4 w-4" /> },
  { id: 'necessidade', label: 'Necessidade', icon: <ClipboardList className="h-4 w-4" /> },
];

export interface OpportunityFormProps {
  /** Estado inicial — passe `stateFromOportunidade(o)` para editar. */
  initialState?: OpportunityFormState;
  /** Oportunidade original quando editando (usado só p/ contexto visual). */
  original?: Oportunidade;
  onSubmit: (state: OpportunityFormState) => Promise<void> | void;
  onSaveDraft?: (state: OpportunityFormState) => Promise<void> | void;
  onCancel: () => void;
  submitLabel: string;
  isSubmitting?: boolean;
  isSavingDraft?: boolean;
  submitError?: string | null;
}

export const OpportunityForm: React.FC<OpportunityFormProps> = ({
  initialState,
  original,
  onSubmit,
  onSaveDraft,
  onCancel,
  submitLabel,
  isSubmitting,
  isSavingDraft,
  submitError,
}) => {
  const [state, setState] = useState<OpportunityFormState>(initialState ?? EMPTY_STATE);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [activeTab, setActiveTab] = useState<TabId>('geral');

  // Reidrata quando `initialState` mudar (edição carrega assíncrono).
  useEffect(() => {
    if (initialState) setState(initialState);
  }, [initialState]);

  const set = <K extends keyof OpportunityFormState>(k: K, v: OpportunityFormState[K]) => {
    setState((s) => ({ ...s, [k]: v }));
    if (errors[k as string]) setErrors((e) => ({ ...e, [k]: '' }));
  };

  const { data: stages } = useEtapas();
  const { data: origens } = useOrigens();
  const { data: tipos } = useTipos();

  // Autocomplete usa client-side filter; carregamos até 200 registros
  // por catálogo. Se catálogos passarem disso, migramos para busca
  // server-side.
  const { data: clientesResp } = useQuery({
    queryKey: ['picker', 'clientes'],
    queryFn: () => listarClientes({ limit: 200 }),
    staleTime: 60 * 1000,
  });
  const { data: usuariosResp } = useQuery({
    queryKey: ['picker', 'usuarios'],
    queryFn: () => listarUsuarios({ limit: 200 }),
    staleTime: 60 * 1000,
  });
  const { data: contatosResp } = useQuery({
    queryKey: ['picker', 'contatos', state.clientId],
    queryFn: () => listarContatos(state.clientId),
    enabled: !!state.clientId,
    staleTime: 30 * 1000,
  });

  const clienteOptions = useMemo(
    () =>
      (clientesResp?.data ?? []).map((c) => ({
        value: c.id,
        label: c.razaoSocial,
        description: `${c.code} • ${c.documento}`,
      })),
    [clientesResp],
  );

  const usuarioOptions = useMemo(
    () =>
      (usuariosResp?.data ?? []).map((u) => ({
        value: u.id,
        label: u.name,
        description: u.email,
      })),
    [usuariosResp],
  );

  const contatoOptions = useMemo(
    () =>
      (contatosResp?.data ?? [])
        .filter((c) => c.status === 'ativo')
        .map((c) => ({
          value: c.id,
          label: c.nome,
          description: c.email,
        })),
    [contatosResp],
  );

  const origemOptions = useMemo(
    () =>
      (origens ?? [])
        .filter((o) => o.active)
        .map((o) => ({ value: o.id, label: o.label })),
    [origens],
  );
  const tipoOptions = useMemo(
    () =>
      (tipos ?? []).filter((t) => t.active).map((t) => ({ value: t.id, label: t.label })),
    [tipos],
  );

  // Reset do contato quando o cliente muda — evita FK inválida.
  const previousClient = React.useRef(state.clientId);
  useEffect(() => {
    if (previousClient.current !== state.clientId && previousClient.current !== '') {
      setState((s) => ({ ...s, contatoPrincipalId: '' }));
    }
    previousClient.current = state.clientId;
  }, [state.clientId]);

  const handleSubmit = async () => {
    const validation = validateForm(state);
    setErrors(validation);
    if (Object.keys(validation).length === 0) {
      await onSubmit(state);
    } else {
      // Auto-navega para a aba com o primeiro erro para o usuário ver.
      if (validation.nome || validation.clientId || validation.contatoPrincipalId || validation.responsavelId || validation.origemId || validation.tipoId) {
        setActiveTab('geral');
      } else if (validation.produtoServico || validation.necessidade) {
        setActiveTab('necessidade');
      } else if (validation.probabilidade) {
        setActiveTab('financeiro');
      }
    }
  };

  const handleSaveDraft = async () => {
    if (!onSaveDraft) return;
    // Rascunho aceita quase tudo — validação mínima: nome ou cliente
    if (!state.nome.trim() && !state.clientId) {
      setErrors({ nome: 'Informe ao menos o nome ou o cliente para salvar rascunho.' });
      setActiveTab('geral');
      return;
    }
    await onSaveDraft(state);
  };

  // Índice da aba atual — usado pela navegação prev/next em mobile
  // (Fatia 19 §17.1: abas viram telas separadas em <768px).
  const currentTabIndex = TABS.findIndex((t) => t.id === activeTab);
  const goPrevTab = () => {
    if (currentTabIndex > 0) setActiveTab(TABS[currentTabIndex - 1].id);
  };
  const goNextTab = () => {
    if (currentTabIndex < TABS.length - 1) setActiveTab(TABS[currentTabIndex + 1].id);
  };
  const isFirstTab = currentTabIndex === 0;
  const isLastTab = currentTabIndex === TABS.length - 1;
  const currentTab = TABS[currentTabIndex];

  return (
    <div className="space-y-4">
      {/* Step indicator — visível SÓ em mobile (<768px).
          Desktop mantém a barra de tabs abaixo. */}
      <div className="md:hidden">
        <div className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-2">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 text-sm font-medium text-gray-900">
              {currentTab.icon}
              {currentTab.label}
            </div>
            <span className="text-xs font-medium text-gray-500">
              Etapa {currentTabIndex + 1} de {TABS.length}
            </span>
          </div>
          {/* Barra de progresso visual */}
          <div className="mt-2 h-1 w-full rounded-full bg-gray-200">
            <div
              className="h-1 rounded-full bg-blue-600 transition-all"
              style={{ width: `${((currentTabIndex + 1) / TABS.length) * 100}%` }}
            />
          </div>
        </div>
      </div>

      {/* Tabs — visíveis em desktop (>=768px) */}
      <div className="hidden border-b border-gray-200 md:block">
        <nav className="-mb-px flex gap-4" aria-label="Abas do formulário">
          {TABS.map((t) => (
            <button
              key={t.id}
              type="button"
              onClick={() => setActiveTab(t.id)}
              className={`inline-flex items-center gap-1 border-b-2 px-1 py-2 text-sm font-medium ${
                activeTab === t.id
                  ? 'border-blue-600 text-blue-700'
                  : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700'
              }`}
              aria-current={activeTab === t.id ? 'page' : undefined}
            >
              {t.icon} {t.label}
            </button>
          ))}
        </nav>
      </div>

      {submitError && (
        <Alert variant="error">
          <div className="flex items-start gap-2">
            <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
            <span>{submitError}</span>
          </div>
        </Alert>
      )}

      {/* Aba: Dados Gerais */}
      {activeTab === 'geral' && (
        <div className="space-y-4">
          <TextInput
            label="Nome da oportunidade"
            value={state.nome}
            onChange={(e) => set('nome', e.target.value)}
            error={errors.nome}
          />

          <AutocompleteSelect
            label="Cliente"
            options={clienteOptions}
            value={state.clientId}
            onChange={(v) => set('clientId', v)}
            error={errors.clientId}
          />

          <AutocompleteSelect
            label="Contato principal"
            options={contatoOptions}
            value={state.contatoPrincipalId}
            onChange={(v) => set('contatoPrincipalId', v)}
            error={errors.contatoPrincipalId}
            disabled={!state.clientId}
          />
          {!state.clientId && (
            <p className="text-xs text-gray-500">
              Selecione o cliente primeiro para carregar os contatos ativos.
            </p>
          )}

          <AutocompleteSelect
            label="Responsável comercial"
            options={usuarioOptions}
            value={state.responsavelId}
            onChange={(v) => set('responsavelId', v)}
            error={errors.responsavelId}
          />

          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <AutocompleteSelect
              label="Origem"
              options={origemOptions}
              value={state.origemId}
              onChange={(v) => set('origemId', v)}
              error={errors.origemId}
            />
            <AutocompleteSelect
              label="Tipo de oportunidade"
              options={tipoOptions}
              value={state.tipoId}
              onChange={(v) => set('tipoId', v)}
              error={errors.tipoId}
            />
          </div>

          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <AutocompleteSelect
              label="Prioridade"
              options={PRIORIDADE_OPTIONS.map((o) => ({ value: o.value, label: o.label }))}
              value={state.prioridade}
              onChange={(v) => set('prioridade', v as Prioridade)}
            />
            <AutocompleteSelect
              label="Etapa inicial (opcional)"
              options={[
                { value: '', label: 'Automática (primeira aberta)' },
                ...((stages ?? [])
                  .filter((s) => s.active && s.outcomeType === 'aberta')
                  .map((s) => ({ value: s.id, label: s.label }))),
              ]}
              value={state.stageId}
              onChange={(v) => set('stageId', v)}
            />
          </div>

          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="isConfidential"
              checked={state.isConfidential}
              onChange={(e) => set('isConfidential', e.target.checked)}
              className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            />
            <label htmlFor="isConfidential" className="text-sm text-gray-700">
              Oportunidade confidencial (acesso restrito ao responsável, participantes e
              perfis de gestão)
            </label>
          </div>
        </div>
      )}

      {/* Aba: Financeiro */}
      {activeTab === 'financeiro' && (
        <div className="space-y-4">
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <TextInput
              label="Valor estimado (BRL)"
              type="number"
              value={state.valorEstimado}
              onChange={(e) => set('valorEstimado', e.target.value)}
              error={errors.valorEstimado}
              icon={<Target className="h-4 w-4 text-gray-400" />}
            />
            <TextInput
              label="Custo estimado (BRL)"
              type="number"
              value={state.custoEstimado}
              onChange={(e) => set('custoEstimado', e.target.value)}
              error={errors.custoEstimado}
            />
          </div>

          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <TextInput
              label="Moeda"
              value={state.moeda}
              onChange={(e) => set('moeda', e.target.value)}
              error={errors.moeda}
            />
            <TextInput
              label="Probabilidade (%) — opcional"
              type="number"
              value={state.probabilidade}
              onChange={(e) => set('probabilidade', e.target.value)}
              error={errors.probabilidade}
            />
          </div>

          <TextInput
            label="Data prevista de fechamento"
            type="date"
            value={state.expectedClosingDate}
            onChange={(e) => set('expectedClosingDate', e.target.value)}
            error={errors.expectedClosingDate}
          />

          {original?.valorPonderado && (
            <p className="text-xs text-gray-500">
              Valor ponderado atual (calculado):{' '}
              <span className="font-mono">{original.valorPonderado}</span>
              {original.margemPercentual != null && (
                <> • Margem: <span className="font-mono">{original.margemPercentual}%</span></>
              )}
            </p>
          )}
        </div>
      )}

      {/* Aba: Necessidade */}
      {activeTab === 'necessidade' && (
        <div className="space-y-4">
          <TextInput
            label="Produto / Serviço / Categoria"
            value={state.produtoServico}
            onChange={(e) => set('produtoServico', e.target.value)}
            error={errors.produtoServico}
          />
          <Textarea
            label="Necessidade (mín. 10 caracteres)"
            rows={4}
            value={state.necessidade}
            onChange={(e) => set('necessidade', e.target.value)}
            error={errors.necessidade}
            showCharCount
            minLength={10}
          />
          <Textarea
            label="Escopo (opcional)"
            rows={3}
            value={state.escopo}
            onChange={(e) => set('escopo', e.target.value)}
          />
          <Textarea
            label="Observações (opcional)"
            rows={3}
            value={state.observacoes}
            onChange={(e) => set('observacoes', e.target.value)}
          />
        </div>
      )}

      {/* Footer — navegação prev/next em mobile + ações. Em desktop,
          só as ações aparecem alinhadas à direita. */}
      <div className="border-t border-gray-200 pt-4">
        {/* Mobile: navegação entre etapas em barra separada */}
        <div className="mb-3 flex items-center justify-between gap-2 md:hidden">
          <button
            type="button"
            onClick={goPrevTab}
            disabled={isFirstTab}
            className="inline-flex items-center gap-1 rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 disabled:opacity-40"
          >
            <ChevronLeft className="h-4 w-4" />
            Anterior
          </button>
          <button
            type="button"
            onClick={goNextTab}
            disabled={isLastTab}
            className="inline-flex items-center gap-1 rounded-md border border-blue-300 bg-blue-50 px-3 py-2 text-sm font-medium text-blue-700 disabled:opacity-40"
          >
            Próxima
            <ChevronRight className="h-4 w-4" />
          </button>
        </div>

        {/* Ações — sempre visíveis; em mobile, ficam abaixo dos prev/next.
            Em mobile o "Efetivar" só aparece na última etapa para evitar
            confusão (mas Cancelar/Rascunho ficam sempre). */}
        <div className="flex flex-wrap items-center justify-end gap-2">
          <button
            type="button"
            onClick={onCancel}
            className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            Cancelar
          </button>
          {onSaveDraft && (
            <button
              type="button"
              onClick={handleSaveDraft}
              disabled={isSavingDraft || isSubmitting}
              className="rounded-md border border-blue-200 bg-blue-50 px-4 py-2 text-sm font-medium text-blue-700 hover:bg-blue-100 disabled:opacity-50"
            >
              {isSavingDraft ? 'Salvando rascunho…' : 'Salvar rascunho'}
            </button>
          )}
          {/* Efetivar: em mobile só na última etapa; em desktop sempre.
              O `handleSubmit` já valida cross-tab e navega para a aba
              com erro se preciso, então o botão nunca "trava" cadastro. */}
          <button
            type="button"
            onClick={handleSubmit}
            disabled={isSubmitting || isSavingDraft}
            className={`rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50 ${
              !isLastTab ? 'hidden md:inline-block' : ''
            }`}
          >
            {isSubmitting ? 'Enviando…' : submitLabel}
          </button>
        </div>
      </div>
    </div>
  );
};

export { stateFromOportunidade, EMPTY_STATE as OPPORTUNITY_FORM_EMPTY_STATE };
